import React from 'react';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { createJSONStorage } from 'zustand/middleware';
import type { StateStorage } from 'zustand/middleware';
import { useSession } from 'next-auth/react';
import {
  fetchUserByEmailOrThrow,
  fetchUserByProviderUsername,
} from '@/apis/fetchUser';
import { shouldRedirectMissingUserToOnboarding } from '@/lib/auth/joinWorkspaceInviteFlow';
import { useAccountMismatchStore } from '@/state/accountMismatchState';
import useUserStore from '@/state/userState';
import useOrganizationStore from '@/state/orgState';
import useTeamStore, { Team } from '@/state/teamState';
import User from '@/types/userType';
import Organization from '@/types/organizationType';
import { useAuthStore } from '@/state/authState';

// Combined user state interface
interface UserContextState {
  // User data
  user:
    | (User & {
        name?: string | null;
        email?: string | null;
        image?: string | null;
        provider?: string;
        accessToken?: string;
        username?: string;
        ghUserName?: string;
        glUserName?: string;
      })
    | null;

  // Organization data
  organization: Organization | null;

  // Team data
  team: Team | null;

  // Authentication status
  status: 'authenticated' | 'loading' | 'unauthenticated';

  // Loading states
  isUserLoading: boolean;
  isOrgLoading: boolean;
  isTeamLoading: boolean;

  // True once fetchOrganization has finished a full resolution attempt
  // (OrganizationUUID lookup plus the legacy OrgName fallback) without
  // erroring. Lets route guards tell "this user has no workspace" apart from
  // "the org just has not loaded yet". Stays false on fetch errors so a down
  // backend never reads as an org-less account.
  orgResolutionDone: boolean;

  // Error states
  userError: string | null;
  orgError: string | null;
  teamError: string | null;

  // Actions
  setUser: (user: User | null) => void;
  setOrganization: (org: Organization | null) => void;
  setTeam: (team: Team | null) => void;
  setStatus: (status: 'authenticated' | 'loading' | 'unauthenticated') => void;
  setUserLoading: (loading: boolean) => void;
  setOrgLoading: (loading: boolean) => void;
  setTeamLoading: (loading: boolean) => void;
  setUserError: (error: string | null) => void;
  setOrgError: (error: string | null) => void;
  setTeamError: (error: string | null) => void;
  fetchUser: (sessionUser: any) => Promise<void>;
  fetchOrganization: () => Promise<void>;
  fetchTeam: () => void;
  /** After invite join: seed stores + force org re-resolve before /home. */
  hydrateJoinedUser: (user: User) => Promise<void>;
  reset: () => void;
  clearUser: () => void;
  clearOrganization: () => void;
}

// Global state to prevent multiple API calls
const GLOBAL_STATE = {
  orgFetchInProgress: false,
  orgInitialized: false,
  userFetchInProgress: false,
  userInitialized: false,
  userEmail: null as string | null,
};

const noopStorage: StateStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
};

const localStorageOrNoop = (): StateStorage =>
  typeof window !== 'undefined' ? window.localStorage : noopStorage;

export const redirectClient = (path: string): void => {
  if (typeof window !== 'undefined') {
    window.location.assign(path);
  }
};

// Zustand store for user context
const useUserContextStore = create<UserContextState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        user: null,
        organization: null,
        team: null,
        status: 'loading',
        isUserLoading: false,
        isOrgLoading: false,
        isTeamLoading: false,
        orgResolutionDone: false,
        userError: null,
        orgError: null,
        teamError: null,

        // Actions
        setUser: (user) =>
          set((state) => {
            // Early exit if user is identical to prevent unnecessary updates
            if (JSON.stringify(state.user) === JSON.stringify(user)) {
              return state;
            }

            if (state.user?.UserUUID && user && !user.UserUUID) {
              return {
                user: {
                  ...state.user,
                  ...user,
                  UserUUID: state.user?.UserUUID,
                },
              };
            }

            return { user };
          }),
        setOrganization: (organization) => set({ organization }),
        setTeam: (team) => set({ team }),
        setStatus: (status) => set({ status }),
        setUserLoading: (isUserLoading) => set({ isUserLoading }),
        setOrgLoading: (isOrgLoading) => set({ isOrgLoading }),
        setTeamLoading: (isTeamLoading) => set({ isTeamLoading }),
        setUserError: (userError) => set({ userError }),
        setOrgError: (orgError) => set({ orgError }),
        setTeamError: (teamError) => set({ teamError }),

        // Fetch user data
        fetchUser: async (sessionUser) => {
          if (!sessionUser?.email) return;

          // Skip if already fetching or fetched this user
          if (
            GLOBAL_STATE.userFetchInProgress ||
            (GLOBAL_STATE.userInitialized &&
              GLOBAL_STATE.userEmail === sessionUser.email)
          ) {
            return;
          }

          GLOBAL_STATE.userFetchInProgress = true;
          GLOBAL_STATE.userEmail = sessionUser.email;
          set({ isUserLoading: true, userError: null });
          try {
            const provider = (sessionUser as any)?.provider as
              string | undefined;
            const username =
              (sessionUser as any)?.username ??
              (sessionUser as any)?.ghUserName ??
              (sessionUser as any)?.glUserName ??
              (sessionUser as any)?.bbUserName;
            const providerUserId = (sessionUser as any)?.id as
              string | undefined;
            const isGithub = (provider ?? '').toLowerCase() === 'github';

            let existingUser: User | undefined;

            if (isGithub) {
              // GitHub only: durable numeric id (then username) before email.
              // Adopt the row; do not send EMU IdP email drift to
              // /account-mismatch (that path rewrote the stored address).
              existingUser = await fetchUserByProviderUsername(
                username,
                provider,
                {},
                providerUserId
              );
              if (!existingUser?.UserUUID) {
                existingUser = await fetchUserByEmailOrThrow(
                  sessionUser.email,
                  {}
                );
              }
              if (existingUser?.UserUUID) {
                GLOBAL_STATE.userInitialized = true;
                useUserStore.getState().setPureUser(existingUser);
                set({ user: existingUser, userError: null });
                return;
              }
            } else {
              // Non-GitHub: unchanged prior behavior. Email first; on miss,
              // username match still routes to /account-mismatch.
              // Throwing variant: a backend that is down must land in the
              // catch below, not the "go to /onboarding" branch.
              existingUser = await fetchUserByEmailOrThrow(
                sessionUser.email,
                {}
              );
              if (existingUser?.UserUUID) {
                GLOBAL_STATE.userInitialized = true;
                useUserStore.getState().setPureUser(existingUser);
                set({ user: existingUser });
                return;
              }
            }

            GLOBAL_STATE.userInitialized = true;

            const mismatchStore = useAccountMismatchStore.getState();
            const alreadyOnMismatchPage =
              typeof window !== 'undefined' &&
              window.location.pathname === '/account-mismatch';

            if (!mismatchStore.hasMismatch && !alreadyOnMismatchPage) {
              // Non-GitHub only: restore username → /account-mismatch.
              if (!isGithub && username) {
                const userByUsername = await fetchUserByProviderUsername(
                  username,
                  provider,
                  {}
                );

                if (
                  userByUsername?.UserUUID &&
                  userByUsername?.OrganizationUUID
                ) {
                  const returnUrl =
                    typeof window !== 'undefined'
                      ? `${window.location.pathname}${window.location.search}`
                      : null;
                  mismatchStore.setMismatch({
                    existingUser: userByUsername,
                    newEmail: sessionUser.email,
                    provider: provider ?? 'unknown',
                    username,
                    returnUrl,
                  });

                  redirectClient('/account-mismatch');
                  return;
                }
              }

              // Invite login/signup keep a join-workspace callback in the
              // query; pathname-only checks missed that and bounced to
              // /onboarding before addMember could run.
              const pathname =
                typeof window !== 'undefined' ? window.location.pathname : '';
              const search =
                typeof window !== 'undefined' ? window.location.search : '';
              if (shouldRedirectMissingUserToOnboarding(pathname, search)) {
                redirectClient('/onboarding');
                return;
              }
            }
          } catch (error: any) {
            const errorMessage =
              error instanceof Error ? error.message : 'Failed to fetch user';

            // Check if it's an auth error (401/403)
            const isAuthError =
              error?.response?.status === 401 ||
              error?.response?.status === 403 ||
              error?.status === 401 ||
              error?.status === 403;

            if (isAuthError) {
              // Don't set userError for auth errors - let httpAdapter handle redirect
              console.error('Authentication error fetching user:', error);
              // Mark this email as "attempted" even on failure. Leaving this
              // unset meant the guard at the top of fetchUser() (userFetchInProgress
              // || (userInitialized && userEmail match)) never blocked a retry after
              // an auth error, so anything re-invoking fetchUser (e.g. a re-render)
              // hammered the backend in a tight loop instead of failing once.
              // reset() clears this on real sign-out/sign-in.
              GLOBAL_STATE.userInitialized = true;
              // Clear auth state to trigger re-authentication
              if (typeof window !== 'undefined') {
                useAuthStore.getState().clearAllAuth();
              }
            } else {
              set({ userError: errorMessage });
              console.error('User fetch error:', error);
            }
          } finally {
            GLOBAL_STATE.userFetchInProgress = false;
            set({ isUserLoading: false });
          }
        },

        hydrateJoinedUser: async (joinedUser) => {
          if (!joinedUser?.UserUUID) return;

          GLOBAL_STATE.userInitialized = true;
          GLOBAL_STATE.userEmail = joinedUser.Email || GLOBAL_STATE.userEmail;
          GLOBAL_STATE.orgInitialized = false;
          GLOBAL_STATE.orgFetchInProgress = false;

          useUserStore.getState().setPureUser(joinedUser);
          set({
            user: joinedUser,
            organization: null,
            orgResolutionDone: false,
            userError: null,
            orgError: null,
          });

          await get().fetchOrganization();
        },

        // Fetch organization data
        fetchOrganization: async () => {
          const { user } = get();
          if (!user?.UserUUID) return;

          if (GLOBAL_STATE.orgInitialized || GLOBAL_STATE.orgFetchInProgress)
            return;

          GLOBAL_STATE.orgFetchInProgress = true;
          set({ isOrgLoading: true, orgError: null });

          try {
            const { setOrganizationByUUID, setOrganizationByName } =
              useOrganizationStore.getState();

            if (user.OrganizationUUID) {
              await setOrganizationByUUID(user.OrganizationUUID);
              GLOBAL_STATE.orgInitialized = true;
            } else {
              const orgName =
                typeof window !== 'undefined'
                  ? window.localStorage.getItem('OrgName')
                  : null;
              if (orgName) {
                await setOrganizationByName(orgName);
                GLOBAL_STATE.orgInitialized = true;
              }
            }

            // Update context with organization data.
            // Only claim resolution when this ran against a freshly fetched
            // user. On boot the persisted user rehydrates first and can carry
            // no OrganizationUUID, which skips both lookups above and would
            // otherwise report an empty org as "resolved" -- sending a user
            // with a perfectly good workspace to /onboarding.
            //
            // setOrganizationByUUID soft-fails to emptyOrganization (no throw),
            // so if the user row already has OrganizationUUID but the org
            // payload is still empty, keep orgResolutionDone false. Do not
            // tell DashboardLayout "no workspace".
            const orgData = useOrganizationStore.getState().organization;
            const orgLoaded = !!(orgData?.OrgUUID || orgData?.OrgName);
            const userHasNoOrg = !user.OrganizationUUID;
            set({
              organization: orgData,
              orgResolutionDone:
                GLOBAL_STATE.userInitialized && (orgLoaded || userHasNoOrg),
            });
          } catch (error: any) {
            const errorMessage =
              error instanceof Error
                ? error.message
                : 'Failed to fetch organization';

            // Check if it's an auth error (401/403)
            const isAuthError =
              error?.response?.status === 401 ||
              error?.response?.status === 403 ||
              error?.status === 401 ||
              error?.status === 403;

            if (isAuthError) {
              // Don't set orgError for auth errors - let httpAdapter handle redirect
              console.error(
                'Authentication error fetching organization:',
                error
              );
              // Same "mark as attempted" reasoning as fetchUser's isAuthError
              // branch above: without this, orgFetchInProgress/orgInitialized
              // never gate a retry after an auth error, so a re-invocation
              // hammers the backend instead of failing once.
              GLOBAL_STATE.orgInitialized = true;
              // Clear auth state to trigger re-authentication
              if (typeof window !== 'undefined') {
                useAuthStore.getState().clearAllAuth();
              }
            } else {
              set({ orgError: errorMessage });
              console.error('Failed to initialize organization:', error);
            }
          } finally {
            GLOBAL_STATE.orgFetchInProgress = false;
            set({ isOrgLoading: false });
          }
        },

        // Fetch team data
        fetchTeam: () => {
          const { user, organization } = get();
          if (
            !user?.TeamUUID ||
            !organization?.OrgUUID ||
            !GLOBAL_STATE.orgInitialized
          )
            return;
        },
        clearUser: () => {
          set({ user: null });
        },
        clearOrganization: () => {
          set({ organization: null, orgResolutionDone: false });
        },
        reset: () => {
          set({
            user: null,
            organization: null,
            team: null,
            status: 'loading',
            isUserLoading: false,
            isOrgLoading: false,
            isTeamLoading: false,
            orgResolutionDone: false,
            userError: null,
            orgError: null,
            teamError: null,
          });

          // The org lives in TWO stores. `organization: null` above clears only
          // THIS one; useOrganizationStore is a separate module-level singleton
          // that survives, and nothing on it identifies which user it belongs
          // to. Leaving it behind meant a reset could install a new user while
          // the previous tenant's org was still readable, and every consumer
          // that treats that store as "the current org" would read it.
          //
          // Eight of the nine reset() callers already hand-paired this call
          // (httpAdapter x2, sign-out x3, org switch, account mismatch) — the
          // duplication was the tell. The ninth, WorkspaceSetup's create
          // rollback, did not, and its comment there asserts reset() "clears
          // user/org state", which is exactly the assumption that did not hold.
          // Clearing here makes it true for every caller instead of eight of
          // them, and the existing paired calls stay correct: clearOrganization
          // is idempotent.
          useOrganizationStore.getState().clearOrganization();

          // Reset global state
          GLOBAL_STATE.orgFetchInProgress = false;
          GLOBAL_STATE.orgInitialized = false;
          GLOBAL_STATE.userFetchInProgress = false;
          GLOBAL_STATE.userInitialized = false;
          GLOBAL_STATE.userEmail = null;
        },
      }),
      {
        name: 'user-context-storage',
        storage: createJSONStorage(localStorageOrNoop),
        partialize: (state) => {
          // accessToken is dropped: the credential lives in an httpOnly
          // cookie now, so it must not land in localStorage.
          if (!state.user) {
            return { user: state.user, status: state.status };
          }
          const { accessToken: _accessToken, ...userWithoutToken } = state.user;
          return { user: userWithoutToken, status: state.status };
        },
        onRehydrateStorage: () => (state, error) => {
          if (error) {
            console.error('Error rehydrating user context:', error);
          }
          // Reset global state on rehydration
          GLOBAL_STATE.orgFetchInProgress = false;
          GLOBAL_STATE.orgInitialized = false;
          GLOBAL_STATE.userFetchInProgress = false;
          GLOBAL_STATE.userInitialized = false;
          GLOBAL_STATE.userEmail = null;
        },
      }
    ),
    {
      name: 'user-context-store',
    }
  )
);

// Hook to use the Zustand store directly
export const useUserContext = () => {
  return useUserContextStore();
};

// Hook for just the state (convenience)
export const useUserContextState = () => {
  return useUserContextStore((state) => ({
    user: state.user,
    organization: state.organization,
    team: state.team,
    status: state.status,
    isUserLoading: state.isUserLoading,
    isOrgLoading: state.isOrgLoading,
    isTeamLoading: state.isTeamLoading,
    userError: state.userError,
    orgError: state.orgError,
    teamError: state.teamError,
  }));
};

// Hook for just the actions (convenience)
export const useUserContextActions = () => {
  return useUserContextStore((state) => ({
    fetchUser: state.fetchUser,
    fetchOrganization: state.fetchOrganization,
    fetchTeam: state.fetchTeam,
    reset: state.reset,
  }));
};

// Hook to sync with existing stores
export const useUserContextSync = () => {
  const { setUser, setOrganization, setTeam, setStatus } =
    useUserContextStore();
  const { user: userStore } = useUserStore();
  const { organization: orgStore } = useOrganizationStore();
  const { team: teamStore } = useTeamStore();
  const { data: session, status } = useSession();

  // Sync data from existing stores to context - only if something actually changed
  React.useEffect(() => {
    const state = useUserContextStore.getState();

    // Only update if data is different to prevent infinite loops
    const mergedUser =
      userStore || session?.user
        ? { ...(userStore ?? {}), ...(session?.user ?? {}) }
        : null;
    const userChanged =
      JSON.stringify(state.user) !== JSON.stringify(mergedUser);
    const orgChanged =
      JSON.stringify(state.organization) !== JSON.stringify(orgStore);
    const teamChanged =
      JSON.stringify(state.team) !== JSON.stringify(teamStore);
    const statusChanged = state.status !== status;

    if (userChanged) setUser(mergedUser);
    if (orgChanged) setOrganization(orgStore);
    if (teamChanged) setTeam(teamStore);
    if (statusChanged) setStatus(status);
  }, [
    userStore,
    orgStore,
    teamStore,
    status,
    session?.user,
    setUser,
    setOrganization,
    setTeam,
    setStatus,
  ]);
};

export default useUserContextStore;
